import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Skeleton, Chip,
  Avatar, Button, LinearProgress, Stack
} from '@mui/material';
import {
  BookOpen, CheckCircle, Award, Clock, TrendingUp,
  Play, ArrowRight, RefreshCw, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BG = '#09090b';
const CARD_SX = { background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' };
const T_PRIMARY = '#fafafa';
const T_SECONDARY = '#a1a1aa';
const T_MUTED = '#71717a';

const StatCard = ({ icon: Icon, label, value, sub, color, loading }) => (
  <Card sx={{ ...CARD_SX, p: 0 }}>
    <CardContent sx={{ p: 2.5 }}>
      {loading ? (
        <>
          <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 1.5 }} />
          <Skeleton width="60%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }} />
          <Skeleton width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
        </>
      ) : (
        <Stack spacing={1.5}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={20} color={color} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={T_PRIMARY}>{value}</Typography>
            <Typography variant="body2" color={T_SECONDARY}>{label}</Typography>
            {sub && <Typography variant="caption" color={T_MUTED}>{sub}</Typography>}
          </Box>
        </Stack>
      )}
    </CardContent>
  </Card>
);

const CourseRow = ({ course, onContinue }) => {
  const progress = course.progress || 0;
  return (
    <Box sx={{ p: 2, ...CARD_SX, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2,
      '&:hover': { borderColor: 'rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' },
      transition: 'all .2s', cursor: 'pointer' }}
      onClick={() => onContinue(course)}
    >
      <Avatar sx={{ bgcolor: '#6366f133', color: '#6366f1', width: 44, height: 44, borderRadius: 2, fontSize: 18 }}>
        {(course.title?.[0] || 'C').toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} color={T_PRIMARY} noWrap>{course.title}</Typography>
        <Typography variant="caption" color={T_MUTED}>{course.instructor?.name || 'QoreNova'}</Typography>
        <LinearProgress
          variant="determinate" value={progress}
          sx={{ mt: 0.8, height: 4, borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': { bgcolor: progress === 100 ? '#10b981' : '#6366f1', borderRadius: 4 }
          }}
        />
        <Typography variant="caption" color={T_MUTED}>{progress}% complete</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Chip
          label={progress === 100 ? 'Completed' : 'In Progress'}
          size="small"
          sx={{ bgcolor: progress === 100 ? '#10b98122' : '#6366f122',
                color: progress === 100 ? '#10b981' : '#a5b4fc', fontSize: 11 }}
        />
      </Box>
    </Box>
  );
};

const ActivityItem = ({ activity }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5,
    borderBottom: '1px solid rgba(255,255,255,0.05)', '&:last-child': { borderBottom: 'none' } }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1', mt: 0.7, flexShrink: 0 }} />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" color={T_SECONDARY}>{activity.message || 'Course activity'}</Typography>
      <Typography variant="caption" color={T_MUTED}>
        {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString() : 'Recently'}
      </Typography>
    </Box>
  </Box>
);

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => { fetchStudentData(); }, []);

  const fetchStudentData = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const headers = token ? { Authorization: 'Bearer ' + token } : {};

      let data = null;
      try {
        const res = await fetch(apiBase + '/api/student/dashboard', { headers });
        if (res.ok) { const j = await res.json(); if (j.success) data = j.data; }
      } catch (_) {}

      if (!data) {
        const res = await fetch('/api/test-dashboards/creator-dashboard-test');
        const j = await res.json();
        if (j.success) {
          data = {
            stats: { enrolledCourses: j.data.courses?.length || 0, completedCourses: 0, certificatesEarned: 0, studyHours: 0 },
            enrolledCourses: (j.data.courses || []).map(c => ({ ...c, progress: 0 })),
            recentActivity: (j.data.recentEnrollments || []).map(e => ({
              message: 'Enrolled in ' + (e.course?.title || 'a course'),
              createdAt: e.createdAt
            })),
            recommended: j.data.courses?.[0] || null
          };
        }
      }
      if (!data) throw new Error('Could not load student data');
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const stats = dashboardData?.stats || {};
  const courses = dashboardData?.enrolledCourses || [];
  const activity = dashboardData?.recentActivity || [];
  const recommended = dashboardData?.recommended;

  const statCards = [
    { icon: BookOpen, label: 'Enrolled Courses', value: stats.enrolledCourses ?? 0, sub: 'Active enrollments', color: '#6366f1' },
    { icon: CheckCircle, label: 'Completed', value: stats.completedCourses ?? 0, sub: 'Courses finished', color: '#10b981' },
    { icon: Award, label: 'Certificates', value: stats.certificatesEarned ?? 0, sub: 'Earned', color: '#f59e0b' },
    { icon: Clock, label: 'Study Hours', value: stats.studyHours ?? 0, sub: 'Total learning time', color: '#06b6d4' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: T_PRIMARY, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color={T_PRIMARY}>
            Welcome back, {user?.firstName || user?.name || 'Learner'} 👋
          </Typography>
          <Typography variant="body2" color={T_MUTED}>Track your progress and keep learning.</Typography>
        </Box>
        <Button
          onClick={fetchStudentData}
          startIcon={<RefreshCw size={16} />}
          sx={{ color: T_SECONDARY, borderColor: 'rgba(255,255,255,0.1)', border: '1px solid', borderRadius: 2,
            '&:hover': { borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.04)' } }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Box sx={{ ...CARD_SX, p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5,
          bgcolor: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.2)' }}>
          <AlertCircle size={18} color="#f87171" />
          <Typography variant="body2" color="#f87171">{error}</Typography>
          <Button size="small" onClick={fetchStudentData} sx={{ ml: 'auto', color: '#f87171' }}>Retry</Button>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statCards.map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <StatCard {...s} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ ...CARD_SX, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Typography variant="h6" fontWeight={600} color={T_PRIMARY}>My Courses</Typography>
              <Button
                size="small" onClick={() => navigate('/courses')} endIcon={<ArrowRight size={14} />}
                sx={{ color: T_MUTED, fontSize: 12, '&:hover': { color: T_SECONDARY } }}
              >Browse more</Button>
            </Box>

            {loading ? (
              [1, 2, 3].map(i => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                  <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }} />
                    <Skeleton width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                  </Box>
                </Box>
              ))
            ) : courses.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <BookOpen size={40} color={T_MUTED} style={{ margin: '0 auto 12px' }} />
                <Typography variant="body2" color={T_MUTED}>No courses enrolled yet.</Typography>
                <Button variant="contained" size="small" onClick={() => navigate('/courses')}
                  sx={{ mt: 2, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}>
                  Browse Courses
                </Button>
              </Box>
            ) : (
              courses.map(course => (
                <CourseRow key={course._id} course={course}
                  onContinue={(c) => navigate(c._id ? '/studentportal/mycourses/' + c._id : '/courses')} />
              ))
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Box sx={{ ...CARD_SX, p: 3 }}>
              <Typography variant="h6" fontWeight={600} color={T_PRIMARY} mb={2}>Recent Activity</Typography>
              {loading ? (
                [1, 2, 3].map(i => <Skeleton key={i} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 0.5 }} />)
              ) : activity.length === 0 ? (
                <Typography variant="body2" color={T_MUTED} sx={{ py: 2, textAlign: 'center' }}>No recent activity yet</Typography>
              ) : (
                activity.slice(0, 6).map((a, i) => <ActivityItem key={i} activity={a} />)
              )}
            </Box>

            {(loading || recommended) && (
              <Box sx={{ ...CARD_SX, p: 3, background: loading ? undefined : 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(99,102,241,0.04))' }}>
                <Typography variant="caption" color="#a5b4fc" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Recommended Next
                </Typography>
                {loading ? (
                  <>
                    <Skeleton width="80%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', mt: 1, mb: 0.5 }} />
                    <Skeleton width="50%" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                  </>
                ) : (
                  <>
                    <Typography variant="body1" fontWeight={600} color={T_PRIMARY} mt={1}>{recommended?.title || 'Continue Learning'}</Typography>
                    <Typography variant="body2" color={T_MUTED} mb={2}>{recommended?.category || 'Keep building skills'}</Typography>
                    <Button variant="contained" fullWidth startIcon={<Play size={16} />}
                      onClick={() => navigate(recommended?._id ? '/studentportal/mycourses/' + recommended._id : '/courses')}
                      sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}>
                      Continue
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ ...CARD_SX, p: 2.5 }}>
              <Typography variant="h6" fontWeight={600} color={T_PRIMARY} mb={1.5}>Quick Actions</Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Browse Courses', icon: BookOpen, path: '/courses' },
                  { label: 'My Learning Path', icon: TrendingUp, path: '/path-dashboard' },
                  { label: 'My Certificates', icon: Award, path: '/certifications' },
                ].map(({ label, icon: Icon, path }) => (
                  <Button key={label} fullWidth startIcon={<Icon size={16} />} onClick={() => navigate(path)}
                    sx={{ justifyContent: 'flex-start', color: T_SECONDARY, borderRadius: 2,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: T_PRIMARY } }}>
                    {label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentDashboard;
